import React from 'react'
import { reduxForm } from 'redux-form'
import styles from './Forms.css'
import TextInput from './components/TextInput'
import TextAreaInput from './components/TextAreaInput'
import MaskedInput from './components/MaskedInput'
import ReactSelectDropdownInput from './components/ReactSelectDropdownInput'
import {
  emailMask,
  phoneNumberMask,
  getDateOfBirthMask,
  dateValidation,
  emailValidation,
  phoneValidation,
  minLen1Validation,
  notesLenValidation,
  notJustSpacesValidation,
  checkSectionIsVisible,
} from './components/formValidations'
import { getName } from '../Utilities'
import {
  UNASSIGNED_ORDERING_PHYSICIAN_OPTION,
  FORM_SEX_OPTIONS,
  getDateOfBirthFormat,
  PATIENT_FIELD_NAMES,
} from '../../constants'
import { filterIsPhysicianPermission } from '../../store/appReducer'
import InputField, {
  InputFieldPlaceholder,
} from './components/InputField/InputField'
import SaveIcon from 'static/images/icons/content-save.svg'
import Loading from '../../components/Loading'
import { getTeamPatientFieldsConfig } from '../../../common/enrollmentUtils'
import { sortBy } from '../../pages/PatientFile/util'

const getCustomFieldInitialDisabledState = (participantCustomFields = []) =>
  participantCustomFields.reduce(
    (prev, curr) => ({ ...prev, [curr.fieldName]: true }),
    {},
  )

const sortByDisplayName = sortBy('displayName')
class EditPatientForm extends React.Component {
  constructor(props) {
    super(props)
    // Initial disabled state for custom fields
    const customFieldDisabledState = getCustomFieldInitialDisabledState(
      this.props.initialValues.participantCustomFields,
    )
    this.state = {
      teamPatientFieldsConfig: {},
      disabled: {
        firstName: true,
        lastName: true,
        customParticipantID: true,
        sex: true,
        email: true,
        dob: true,
        orderingPhysician: true,
        phone: true,
        notes: true,
        subjectID: true,
        studyID: true,
        siteIdentificationNumber: true,
        ...customFieldDisabledState,
      },
    }
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    const customFieldDisabledState = getCustomFieldInitialDisabledState(
      this.props.initialValues.participantCustomFields,
    )
    if (
      nextProps.submitSucceeded !== this.props.submitSucceeded &&
      nextProps.submitSucceeded === true
    ) {
      this.setState({
        disabled: {
          firstName: true,
          lastName: true,
          middleName: true,
          customParticipantID: true,
          sex: true,
          email: true,
          dob: true,
          socialSecurityNumber: true,
          orderingPhysician: true,
          phone: true,
          notes: true,
          subjectID: true,
          studyID: true,
          siteIdentificationNumber: true,
          ...customFieldDisabledState,
        },
      })
    }
  }

  componentDidMount() {
    /**
     * Getting the teamPatientFields (list of field config objects) form me object
     * and reduce them into a single object with fieldName as key and other config properties object as value
     */
    const { me } = this.props
    const { teams } = me.data
    const teamPatientFieldsConfig = getTeamPatientFieldsConfig(teams)
    this.setState({ teamPatientFieldsConfig })
  }

  toggleDisable = field => {
    this.setState(prevState => ({
      disabled: Object.assign(prevState.disabled, {
        [field]: !prevState.disabled[field],
      }),
    }))
  }

  render() {
    const {
      handleSubmit,
      invalid,
      pristine,
      submitting,
      isSubmitting,
      me,
      physicians,
      initialValues: { participantCustomFields = [] },
    } = this.props

    const { disabled, teamPatientFieldsConfig } = this.state
    let physicianOptions = [ UNASSIGNED_ORDERING_PHYSICIAN_OPTION ]

    if (physicians && physicians.data) {
      physicianOptions = [
        ...physicianOptions,
        ...filterIsPhysicianPermission(
          physicians.data.sortedByLastName.map(id => physicians.data.byId[id]),
        ),
      ]
    }
    /**
     * If all Default Fields are removed from a section, that section should be removed from view, shifting up the other sections
     */
    const canShowNamesSection = checkSectionIsVisible(teamPatientFieldsConfig, [
      PATIENT_FIELD_NAMES.FIRST_NAME,
      PATIENT_FIELD_NAMES.LAST_NAME,
    ])

    const canShowPatientInfoSection = checkSectionIsVisible(teamPatientFieldsConfig, [
      PATIENT_FIELD_NAMES.MRN,
      PATIENT_FIELD_NAMES.EMAIL,
      PATIENT_FIELD_NAMES.SEX,
      PATIENT_FIELD_NAMES.DATE_OF_BIRTH,
      PATIENT_FIELD_NAMES.ORDERING_PHYSICIAN,
      PATIENT_FIELD_NAMES.PHONE,
    ])
    const canShowStudySection = checkSectionIsVisible(teamPatientFieldsConfig, [
      PATIENT_FIELD_NAMES.STUDY_ID,
      PATIENT_FIELD_NAMES.SITE_IDENTIFICATION_NUMBER,
      PATIENT_FIELD_NAMES.SUBJECT_ID,
    ])
    const canShowNoteSection = checkSectionIsVisible(teamPatientFieldsConfig, [ PATIENT_FIELD_NAMES.NOTES ])
    // Sorting by alphabetical order and Generating custom fields based on KPA config.
    const customFieldElements = participantCustomFields
      .filter(({ fieldName }) => teamPatientFieldsConfig[fieldName] && teamPatientFieldsConfig[fieldName].isVisible)
      .sort(sortByDisplayName).map(
        ({ fieldName, displayName, id }) => (
          <div className={styles.formSectionRow} key={id}>
            <InputField
              key={id}
              component={TextInput}
              label={displayName || fieldName}
              name={fieldName}
              placeholder={`Enter ${displayName || fieldName }`}
              disabled={disabled}
              toggleDisable={this.toggleDisable}
              config={teamPatientFieldsConfig[fieldName]}
            />
          </div>
        ),
      )
    return (
      <form onSubmit={handleSubmit}>
        <React.Fragment>
          {
            canShowStudySection && (
              <div className={styles.editPatientSection}>
                <div className={styles.formSectionRow}>
                  <InputField
                    component={TextInput}
                    label="Subject ID"
                    name={PATIENT_FIELD_NAMES.SUBJECT_ID}
                    placeholder="Subject ID"
                    toggleDisable={this.toggleDisable}
                    config={teamPatientFieldsConfig.subjectID}
                    disabled={disabled}
                  />
                  <InputField
                    component={TextInput}
                    label="Study ID"
                    name={PATIENT_FIELD_NAMES.STUDY_ID}
                    placeholder="Study ID"
                    toggleDisable={this.toggleDisable}
                    config={teamPatientFieldsConfig.studyID}
                    disabled={disabled}
                  />
                  <InputField
                    component={TextInput}
                    label="Site Identification Number"
                    name={PATIENT_FIELD_NAMES.SITE_IDENTIFICATION_NUMBER}
                    placeholder="Site Identification Number"
                    toggleDisable={this.toggleDisable}
                    config={teamPatientFieldsConfig.siteIdentificationNumber}
                    disabled={disabled}
                  />
                </div>
              </div>
            )
          }
          {canShowNamesSection && (
            <div className={styles.editPatientSection}>
              <div className={styles.formSectionRow}>
                <InputField
                  component={TextInput}
                  label="First Name"
                  name={PATIENT_FIELD_NAMES.FIRST_NAME}
                  placeholder="Enter First Name"
                  disabled={disabled}
                  toggleDisable={this.toggleDisable}
                  config={teamPatientFieldsConfig.firstName}
                />
                <InputField
                  component={TextInput}
                  label="Last Name"
                  name={PATIENT_FIELD_NAMES.LAST_NAME}
                  placeholder="Enter Last Name"
                  disabled={disabled}
                  toggleDisable={this.toggleDisable}
                  config={teamPatientFieldsConfig.lastName}
                />
                <InputFieldPlaceholder />
              </div>
            </div>
          )}
          {canShowPatientInfoSection && (
            <div className={styles.editPatientSection}>
              <div className={styles.formSectionRow}>
                <InputField
                  component={TextInput}
                  label="Patient MRN"
                  name={PATIENT_FIELD_NAMES.MRN}
                  validate={[ notJustSpacesValidation ]}
                  disabled={disabled}
                  toggleDisable={this.toggleDisable}
                  config={teamPatientFieldsConfig.customParticipantID}
                />
                <InputField
                  component={MaskedInput}
                  mask={emailMask}
                  placeholder="name@example.com"
                  label="Email"
                  name={PATIENT_FIELD_NAMES.EMAIL}
                  validate={[ emailValidation, minLen1Validation ]}
                  disabled={disabled}
                  toggleDisable={this.toggleDisable}
                  config={teamPatientFieldsConfig.email}
                />
                <InputField
                  component={ReactSelectDropdownInput}
                  label="Sex"
                  name={PATIENT_FIELD_NAMES.SEX}
                  placeholder="Select a sex"
                  options={FORM_SEX_OPTIONS}
                  noOptionsMessage="No Sex found"
                  getOptionLabel={p => p.label}
                  getOptionValue={p => p.value}
                  disabled={disabled}
                  toggleDisable={this.toggleDisable}
                  config={teamPatientFieldsConfig.sex}
                />
              </div>
              <div className={styles.formSectionRow}>
                <InputField
                  component={MaskedInput}
                  mask={getDateOfBirthMask(me)}
                  placeholder={getDateOfBirthFormat(me)}
                  label="Date of Birth"
                  name={PATIENT_FIELD_NAMES.DATE_OF_BIRTH}
                  validate={[ dateValidation ]}
                  disabled={disabled}
                  toggleDisable={this.toggleDisable}
                  config={teamPatientFieldsConfig.dob}
                />
                <InputField
                  component={ReactSelectDropdownInput}
                  label="Ordering Provider"
                  name={PATIENT_FIELD_NAMES.ORDERING_PHYSICIAN}
                  isLoading={physicians && physicians.isFetching}
                  isClearable={false}
                  backspaceRemovesValue={false}
                  placeholder="Unassigned"
                  options={physicianOptions}
                  noOptionsMessage="No provider found"
                  getOptionLabel={p =>
                    getName(p.firstName, p.lastName, p.customParticipantID)
                  }
                  getOptionValue={p => p.id}
                  disabled={disabled}
                  toggleDisable={this.toggleDisable}
                  config={teamPatientFieldsConfig.orderingPhysicianMemberID}
                />
                <InputField
                  component={MaskedInput}
                  mask={phoneNumberMask}
                  placeholder="XXX XXX-XXXX"
                  label="Phone Number"
                  name={PATIENT_FIELD_NAMES.PHONE}
                  validate={[ phoneValidation ]}
                  disabled={disabled}
                  toggleDisable={this.toggleDisable}
                  config={teamPatientFieldsConfig.phone}
                />
              </div>
            </div>
          )}
          {/* Custom fields section */}
          {(customFieldElements && customFieldElements.length > 0) && (
            <div className={styles.editPatientSection}>
              {customFieldElements}
            </div>
          )}

          {canShowNoteSection && (
            <div className={styles.editPatientSection}>
              <InputField
                component={TextAreaInput}
                label="Notes"
                name={PATIENT_FIELD_NAMES.NOTES}
                placeholder="Enter Notes"
                validate={[ notesLenValidation ]}
                disabled={disabled}
                toggleDisable={this.toggleDisable}
                config={teamPatientFieldsConfig.notes}
              />
            </div>
          )}
        </React.Fragment>
        <div className={styles.submitWrapper}>
          <div className={styles.buttonWrapper}>
            {submitting || isSubmitting ? (
              <button disabled className={styles.submitLoading}>
                <Loading />
              </button>
            ) : (
              <button
                type="submit"
                disabled={invalid || pristine}
                className={styles.submit}
                data-action="button-edit-profile"
                data-label="save"
              >
                <SaveIcon />
                Save
              </button>
            )}
          </div>
        </div>
      </form>
    )
  }
}

export default reduxForm({
  form: 'editPatientForm',
  enableReinitialize: true,
})(EditPatientForm)



// WEBPACK FOOTER //
// ./src/legacy/containers/Forms/EditPatientForm.jsx